import { useState } from "react";

/**
 * A country's flag, small, beside the country's name.
 *
 * ## Why these are drawn here rather than fetched or typed
 *
 * The obvious answer is the emoji flag — two regional indicator letters, no
 * assets, no component. It does not work. Windows ships no flag glyphs at all,
 * so Chrome and Edge on Windows render `🇫🇷` as the letters **FR**, and the
 * cellar's owner is on Windows. An interface element that silently degrades to
 * two capital letters on the machine it is used from is not an interface
 * element.
 *
 * The next answer was an icon package, and it was rejected in 2026 as a
 * dependency in the browser bundle, a licence to carry, and two hundred and
 * fifty flags shipped to draw six — *a ninth country is a commit, not a
 * release.* **That last part stopped being acceptable at `9.10`**, when the
 * picker opened to every country: a cellar that can hold a Chilean wine and
 * then shows nothing beside it is worse than the package would have been.
 *
 * So the package came back, in the shape the objection was actually about.
 * `flag-icons` is a **devDependency**, MIT, and nothing of it reaches the
 * browser: `scripts/generate-flags.mjs` copies its 1x1 set into
 * `frontend/public/flags/` at build time, and a row fetches the one file its
 * country needs. The bundle carries the eight below and no more.
 *
 * ## Deliberately quiet, and deliberately simplified
 *
 * `design-direction.md` spends the interface's boldness on the drinking window
 * and asks everything else to stay out of its way. Six saturated rectangles per
 * screen, over a hundred rows, would not. So they are the size of a word rather
 * than a button, and they sit at rest — the band is still the thing you see
 * first.
 *
 * They had a hairline border, and it was removed on 2026-09-12 for the reason
 * borders like it usually should be: at this size the ring is a third of what
 * the eye sees, and it read as a frame around a picture rather than as an edge
 * on a flag. What it was guarding against is real but small — of the eight,
 * only the American flag has white touching its edge — and a faint seam on one
 * flag is a cheaper problem than a box drawn around all eight.
 *
 * **They are circles.** Every flag is drawn square and clipped to a disc, which
 * is true of none of them: the tricolours are 3:2, the American flag 19:10. A
 * row of flags at their real ratios is a row of different-sized things, and the
 * eye reads that as disorder rather than as accuracy — a column of identical
 * discs reads as one column. It also removes the problem a rectangle brought
 * with it: a rectangle has corners, corners want an edge drawn round them, and
 * at this size that edge is a third of what you see.
 *
 * The cost is honest and small: squashing 3:2 into 1:1 and then cutting the
 * corners off is not what any of these flags looks like. At the size of a word,
 * beside a wine's name, it is still the only thing that identifies the country
 * faster than reading — which is the only job this element has. Detail that
 * turns to mud is left out for the same reason: Spain's and Portugal's arms,
 * and the stars in the American canton.
 */
/**
 * The eight drawn by hand, which since `9.10` are **overrides rather than the
 * whole set**.
 *
 * Every other country's flag is copied out of `flag-icons` by the build and
 * fetched as a file, so a wine from Chile arrives with a Chilean flag and
 * nobody has to draw one. These eight stay because the generated versions are
 * worse at this size in exactly the way the note above describes: Spain's is
 * 80 kB and four fifths of it is a coat of arms that renders as a smudge
 * inside a disc the size of a word.
 *
 * **So this list is the quality dial, and it is meant to be edited.** Deleting
 * an entry takes the generated flag instead; adding one replaces a generated
 * flag that reads badly. The cost of the arrangement is honest and visible: a
 * hand-simplified Spain sits in the same column as a fully detailed Croatia,
 * and the two were drawn to different rules.
 */
const FLAGS: Record<string, React.ReactNode> = {
  FR: (
    <>
      <rect width="4" height="12" fill="#0055a4" />
      <rect x="4" width="4" height="12" fill="#fff" />
      <rect x="8" width="4" height="12" fill="#ef4135" />
    </>
  ),
  IT: (
    <>
      <rect width="4" height="12" fill="#008c45" />
      <rect x="4" width="4" height="12" fill="#f4f5f0" />
      <rect x="8" width="4" height="12" fill="#cd212a" />
    </>
  ),
  DE: (
    <>
      <rect width="12" height="4" fill="#000" />
      <rect y="4" width="12" height="4" fill="#dd0000" />
      <rect y="8" width="12" height="4" fill="#ffce00" />
    </>
  ),
  // The middle band is double height on the real flag, and that proportion is
  // most of what identifies it, so it is kept even though the arms are not.
  ES: (
    <>
      <rect width="12" height="3" fill="#aa151b" />
      <rect y="3" width="12" height="6" fill="#f1bf00" />
      <rect y="9" width="12" height="3" fill="#aa151b" />
    </>
  ),
  AT: (
    <>
      <rect width="12" height="4" fill="#ed2939" />
      <rect y="4" width="12" height="4" fill="#fff" />
      <rect y="8" width="12" height="4" fill="#ed2939" />
    </>
  ),
  // Two fifths green, three fifths red, and the sphere left off.
  PT: (
    <>
      <rect width="12" height="12" fill="#da291c" />
      <rect width="4.8" height="12" fill="#046a38" />
      <circle cx="4.8" cy="6" r="1.7" fill="#ffe900" stroke="#046a38" strokeWidth="0.5" />
    </>
  ),
  // Thirteen stripes would be hairlines at this size, so seven carry the idea.
  US: (
    <>
      <rect width="12" height="12" fill="#fff" />
      {[0, 2, 4, 6, 8, 10].map((y) => (
        <rect key={y} y={y} width="12" height="1" fill="#b31942" />
      ))}
      <rect width="6" height="7" fill="#0a3161" />
    </>
  ),
  // The canton is schematic: the crosses of the union, and the four stars of
  // the Southern Cross, which is the half that tells it from Australia's.
  NZ: (
    <>
      <rect width="12" height="12" fill="#00247d" />
      <path d="M0 0h6v5H0z" fill="#00247d" />
      <path d="M0 0l6 5M6 0L0 5" stroke="#fff" strokeWidth="1.1" />
      <path d="M3 0v5M0 2.5h6" stroke="#fff" strokeWidth="1.7" />
      <path d="M3 0v5M0 2.5h6" stroke="#cc142b" strokeWidth="0.9" />
      <g fill="#cc142b">
        <circle cx="9.6" cy="3" r="0.7" />
        <circle cx="10.7" cy="6" r="0.7" />
        <circle cx="8.5" cy="7.4" r="0.7" />
        <circle cx="9.7" cy="9.6" r="0.9" />
      </g>
    </>
  ),
};

/** One set of measurements, so the three kinds of disc are the same disc. */
const DISC =
  "inline-block h-[1.15em] w-[1.15em] shrink-0 [clip-path:circle(50%)] align-[-0.18em]";

/**
 * The flag for a country code: drawn here if it is one of the eight, fetched
 * from the build's copy of `flag-icons` if it is not, and a quiet disc with
 * the code in it when neither can be had.
 *
 * **The quiet disc is not a placeholder for a missing feature.** It is what a
 * country looks like when there is no flag to show it with — a code this
 * application has never heard of, or a flag the phone could not fetch because
 * it is standing in a cellar with no signal and has not seen that country
 * before. Both are real, and both are better answered by two small letters
 * than by the browser's broken-image glyph, which says *this page is broken*
 * about a page that is not.
 *
 * It is `aria-hidden` throughout. The country's name follows in the same line,
 * so a screen reader announcing both would say Frankrike twice.
 */
export function Flag({ countryCode }: { countryCode: string }) {
  const drawing = FLAGS[countryCode];
  const [fetchable, setFetchable] = useState(true);

  if (drawing !== undefined) {
    return (
      <svg
        viewBox="0 0 12 12"
        data-flag={countryCode}
        className={DISC}
        aria-hidden="true"
        focusable="false"
      >
        {drawing}
      </svg>
    );
  }

  if (fetchable && /^[A-Za-z]{2}$/.test(countryCode)) {
    return (
      <img
        // `flag-icons` names its files lower case and the code is upper case
        // everywhere else here, so this is the one place the two fold together.
        src={`/flags/${countryCode.toLowerCase()}.svg`}
        data-flag={countryCode}
        className={`${DISC} object-cover`}
        // A flag that is not there is not an error worth a console entry or a
        // retry — it is the case the disc below exists for.
        onError={() => setFetchable(false)}
        alt=""
        aria-hidden="true"
      />
    );
  }

  return (
    <span
      data-flag={countryCode}
      className={`${DISC} bg-line text-muted text-center text-[0.5em] leading-[2.3]`}
      aria-hidden="true"
    >
      {countryCode.slice(0, 2).toUpperCase()}
    </span>
  );
}
